import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "@/components/layout/Header";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Calendar,
  TrendingUp,
  ArrowRight,
  Sparkles,
  Clock,
  RefreshCw,
  BookOpen,
  RotateCcw,
  Loader2,
} from "lucide-react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { useProduct } from "@/hooks/useProduct";
import { fetchDashboardSessions } from "@/store/slices/dashboardSlice";
import { generateQuizThunk, checkQuizStatusThunk, resetQuizState } from "@/store/slices/quizSlice";
import { useToast } from "@/hooks/use-toast";
import {
  selectDashboardLoading,
  selectDashboardError,
  selectDashboardStats,
  selectSessionsWithScores,
} from "@/store/selectors/dashboardSelectors";
import { getMeetingModules } from "@/store/slices/meetingModulesSlice";
import { selectTotalModulesCountRounded } from "@/store/selectors/meetingModulesSelectors";
import { SEOPresets } from "@/components/SEO";

const Dashboard = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const config = useProduct();

  // Redux selectors
  const loading = useAppSelector(selectDashboardLoading);
  const error = useAppSelector(selectDashboardError);
  const stats = useAppSelector(selectDashboardStats);
  const sessionsWithScores = useAppSelector(selectSessionsWithScores);
  const totalModulesCount = useAppSelector(selectTotalModulesCountRounded);
  const { toast } = useToast();
  const quizState = useAppSelector((state) => state.quiz);
  const [generatingQuizId, setGeneratingQuizId] = useState<string | null>(null);

  // Fetch dashboard data on mount
  useEffect(() => {
    dispatch(fetchDashboardSessions(false)); // Use cache if available
    dispatch(getMeetingModules({})); // Fetch modules for count
  }, [dispatch]);

  // Handle refresh
  const handleRefresh = () => {
    dispatch(fetchDashboardSessions(true)); // Force refresh
  };
  const getScoreColor = (score: number) => {
    if (score >= 8) return "text-green-600 dark:text-green-400";
    if (score >= 6) return "text-blue-600 dark:text-blue-400";
    if (score >= 4) return "text-yellow-600 dark:text-yellow-400";
    return "text-orange-600 dark:text-orange-400";
  };
  const getScoreBgColor = (score: number) => {
    if (score >= 8) return "bg-green-500/10 border-green-500/20";
    if (score >= 6) return "bg-blue-500/10 border-blue-500/20";
    if (score >= 4) return "bg-yellow-500/10 border-yellow-500/20";
    return "bg-orange-500/10 border-orange-500/20";
  };

  const handleQuizAction = async (sessionId: string, status: string) => {
    dispatch(resetQuizState(sessionId));

    if (status === "completed") {
      navigate(`/meeting/${sessionId}/quiz/generated`);
      return;
    }

    if (status === "not_started" || status === "error") {
      setGeneratingQuizId(sessionId);
      const resultAction = await dispatch(generateQuizThunk(sessionId));
      
      if (generateQuizThunk.rejected.match(resultAction)) {
        // If generation fails, check if it's because it's already generated
        const statusResult = await dispatch(checkQuizStatusThunk(sessionId));
        setGeneratingQuizId(null);
        
        if (checkQuizStatusThunk.fulfilled.match(statusResult) && statusResult.payload.status === "completed") {
          navigate(`/meeting/${sessionId}/quiz/generated`);
        } else {
          toast({
            title: "Error",
            description: "Failed to start quiz generation.",
            variant: "destructive",
          });
        }
      } else {
        setGeneratingQuizId(null);
        // After successful generation, navigate to the quiz
        navigate(`/meeting/${sessionId}/quiz/generated`);
      }
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {SEOPresets.dashboard}
      <Header />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        {/* Refresh Button */}
        <div className="flex justify-end mb-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleRefresh}
            disabled={loading}
            title="Refresh dashboard"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
        {/* Welcome Section */}
        <div className="mb-12 animate-fade-in">
          <h1 className="text-4xl sm:text-5xl font-extrabold text-foreground mb-4">
            {config.i18n.dashboardPage.welcome}
          </h1>
          <p className="text-xl text-muted-foreground">
            {config.i18n.dashboardPage.subtitle}
          </p>
        </div>

        {/* Stats Overview */}
        {loading && !stats.totalSessions ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12 animate-fade-in">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="border-2 border-border/50">
                <CardContent className="p-6">
                  <div className="animate-pulse">
                    <div className="h-4 bg-muted rounded w-3/4 mb-2"></div>
                    <div className="h-8 bg-muted rounded w-1/2"></div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : error ? (
          <Card className="mb-12 border-red-500/20 bg-red-500/5">
            <CardContent className="p-6 text-center">
              <p className="text-red-500 mb-4">{error}</p>
              <Button onClick={handleRefresh} variant="outline">
                Try Again
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12 animate-fade-in">
            <Card className="border-2 border-border/50 bg-gradient-to-br from-primary/5 to-accent/5">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground font-medium mb-1">
                      {config.i18n.dashboardPage.totalSimulations}
                    </p>
                    <p className="text-3xl font-bold text-foreground">
                      {stats.totalSessions}
                    </p>
                  </div>
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                    <Sparkles className="w-7 h-7 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-2 border-border/50 bg-gradient-to-br from-blue-500/5 to-cyan-500/5">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground font-medium mb-1">
                      {config.i18n.dashboardPage.averageScore}
                    </p>
                    <p className="text-3xl font-bold text-foreground">
                      {stats.averageScore !== null
                        ? `${stats.averageScore}%`
                        : "N/A"}
                    </p>
                  </div>
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center">
                    <TrendingUp className="w-7 h-7 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-2 border-border/50 bg-gradient-to-br from-green-500/5 to-emerald-500/5">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground font-medium mb-1">
                      {config.i18n.dashboardPage.lastPractice}
                    </p>
                    <p className="text-lg font-bold text-foreground">
                      {stats.lastPracticeDate
                        ? new Date(stats.lastPracticeDate).toLocaleDateString(
                            "en-US",
                            {
                              month: "short",
                              day: "numeric",
                            }
                          )
                        : "N/A"}
                    </p>
                  </div>
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center">
                    <Clock className="w-7 h-7 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* CTA Card */}
        <Card className="mb-12 border-2 border-primary/20 bg-gradient-to-br from-primary/10 via-accent/10 to-primary/10 overflow-visible relative animate-fade-in">
          <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-primary/20 to-accent/20 rounded-full blur-3xl" />
          <CardContent className="p-8 sm:p-12 text-center relative z-10 flex flex-col items-center">
            <div className="inline-block mb-4">
              <span className="bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-bold">
                {config.i18n.dashboardPage.ctaEmoji}{" "}
                {config.i18n.dashboardPage.ctaTitle}
              </span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-foreground mb-4">
              {config.i18n.dashboardPage.ctaSubtitle}
            </h2>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
              {config.i18n.dashboardPage.ctaDescription}
            </p>
            <Button
              size="lg"
              onClick={() => navigate("/meeting-simulator")}
              className="bg-primary text-primary-foreground hover:bg-primary/90 text-lg px-8 py-6 h-auto font-bold shadow-lg hover:shadow-xl transition-all hover:scale-105 flex-shrink-0"
            >
              {config.i18n.dashboardPage.ctaButton}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </CardContent>
        </Card>

        {/* Previous Simulations */}
        <div className="animate-fade-in">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-3xl font-bold text-foreground">
              {config.i18n.dashboardPage.recentSessions}
            </h2>
          </div>

          {loading && sessionsWithScores.length === 0 ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <Card key={i} className="border-2 border-border/50">
                  <CardContent className="p-6">
                    <div className="animate-pulse space-y-4">
                      <div className="h-6 bg-muted rounded w-1/3"></div>
                      <div className="h-4 bg-muted rounded w-1/4"></div>
                      <div className="h-10 bg-muted rounded w-32"></div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : sessionsWithScores.length === 0 ? (
            <Card className="border-2 border-border/50">
              <CardContent className="p-12 text-center">
                <Sparkles className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-2xl font-bold text-foreground mb-2">
                  {config.i18n.dashboardPage.emptySessions}
                </h3>
                <p className="text-muted-foreground mb-6">
                  {config.i18n.dashboardPage.emptySessionsDescription}
                </p>
                <Button onClick={() => navigate("/meeting-simulator")}>
                  {config.i18n.dashboardPage.startFirstSession}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {sessionsWithScores.map((session) => {
                const score = session.calculatedScore;
                return (
                  <Card
                    key={session.id}
                    className="group hover:shadow-lg transition-all duration-300 hover:-translate-y-1 border-2 border-border/50 hover:border-primary/30 bg-card"
                  >
                    <CardContent className="p-6">
                      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 mb-2 flex-wrap">
                            <h3 className="text-xl font-bold text-foreground">
                              {session.meeting_name}
                            </h3>
                            <span className="text-xs bg-muted text-muted-foreground px-2 py-1 rounded-full">
                              {session.meeting_category}
                            </span>
                          </div>
                          <div className="flex items-center text-muted-foreground text-sm mb-4">
                            <Calendar className="w-4 h-4 mr-2" />
                            {new Date(session.created_at).toLocaleDateString(
                              "en-US",
                              {
                                weekday: "long",
                                year: "numeric",
                                month: "long",
                                day: "numeric",
                              }
                            )}
                          </div>

                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:max-w-2xl">
                            <div>
                              {session.expressions &&
                                session.expressions.length > 0 && (
                                  <Button
                                    variant="outline"
                                    onClick={() =>
                                      navigate(
                                        `/meeting/${session.id}/practice-expressions?from=dashboard`
                                      )
                                    }
                                    className="w-full"
                                  >
                                    {config.i18n.dashboardPage.topExpressions}
                                  </Button>
                                )}
                            </div>

                            <div>
                              {session.realtime_transcript && (
                                <Button
                                  variant="default"
                                  onClick={() => {
                                    const feedbackId =
                                      session.realtime_transcript!.id;
                                    navigate(
                                      `/meeting/${session.id}/feedback/${feedbackId}`
                                    );
                                  }}
                                  className="w-full"
                                >
                                  {config.i18n.dashboardPage.viewFeedback}
                                </Button>
                              )}
                            </div>

                            {session.realtime_transcript && (
                              <div>
                                <Button
                                  variant={session.quiz_status === "completed" ? "outline" : "default"}
                                  onClick={() => handleQuizAction(session.id, session.quiz_status || "not_started")}
                                  disabled={generatingQuizId === session.id || session.quiz_status === "processing"}
                                  className={session.quiz_status === "completed" ? "w-full text-primary border-primary/20 hover:bg-primary/5 hover:text-primary" : "w-full"}
                                >
                                  {generatingQuizId === session.id || session.quiz_status === "processing" ? (
                                    <>
                                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                      Generating Quiz...
                                    </>
                                  ) : session.quiz_status === "completed" ? (
                                    <>
                                      <RotateCcw className="w-4 h-4 mr-2" />
                                      Retake Quiz
                                    </>
                                  ) : (
                                    <>
                                      <BookOpen className="w-4 h-4 mr-2" />
                                      Start Quiz
                                    </>
                                  )}
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>

                        {score !== null && (
                          <div
                            className={`flex items-center justify-center w-24 h-24 rounded-2xl border-2 ${getScoreBgColor(score)} self-center lg:self-auto shrink-0`}
                          >
                            <div className="text-center">
                              <div
                                className={`text-3xl font-bold ${getScoreColor(score)}`}
                              >
                                {score}
                              </div>
                              <div className="text-xs text-muted-foreground font-medium">
                                {config.i18n.dashboardPage.score}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
export default Dashboard;
